(function () {
  const SELECTOR = '.sutyo-one-slide-reveal[data-section-id]';
  const instances = new WeakMap();

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  function setup(root) {
    if (!root || instances.has(root)) {
      return;
    }

    const slides = Array.from(root.querySelectorAll('[data-reveal-slide]'));
    const stage = root.querySelector('[data-reveal-stage]');
    const sticky = root.querySelector('.sutyo-one-slide-reveal__sticky');
    const liveRegion = root.querySelector('[data-reveal-live]');

    if (!stage || !sticky || !slides.length) {
      return;
    }

    const wheelThreshold = Number(root.dataset.wheelThreshold || 42);
    const swipeThreshold = Number(root.dataset.swipeThreshold || 44);
    const animationDuration = Number(root.dataset.animationDuration || 760);
    const lastIndex = slides.length - 1;
    const wheelOptions = { passive: false };
    const touchMoveOptions = { passive: false };
    let currentIndex = slides.findIndex((slide) => slide.classList.contains('is-current'));
    let wheelDelta = 0;
    let isAnimating = false;
    let isNearViewport = false;
    let touchStartY = 0;
    let touchDeltaY = 0;
    let observer = null;

    if (currentIndex < 0) {
      currentIndex = 0;
    }

    root.style.setProperty('--sutyo-one-slide-animation-duration', `${animationDuration}ms`);

    function announce() {
      if (liveRegion) {
        liveRegion.textContent = `Image ${currentIndex + 1} of ${slides.length}`;
      }
    }

    function setSettledClasses() {
      slides.forEach((slide, index) => {
        slide.classList.remove('is-entering', 'is-leaving', 'is-current', 'is-above', 'is-below');

        if (index < currentIndex) {
          slide.classList.add('is-above');
          slide.setAttribute('aria-hidden', 'true');
        } else if (index > currentIndex) {
          slide.classList.add('is-below');
          slide.setAttribute('aria-hidden', 'true');
        } else {
          slide.classList.add('is-current');
          slide.setAttribute('aria-hidden', 'false');
        }
      });

      announce();
    }

    function goTo(nextIndex) {
      const targetIndex = clamp(nextIndex, 0, lastIndex);

      if (targetIndex === currentIndex || isAnimating) {
        return false;
      }

      const currentSlide = slides[currentIndex];
      const nextSlide = slides[targetIndex];

      isAnimating = true;

      slides.forEach((slide, index) => {
        slide.classList.remove('is-entering', 'is-leaving', 'is-current', 'is-above', 'is-below');

        if (index < Math.min(currentIndex, targetIndex)) {
          slide.classList.add('is-above');
          slide.setAttribute('aria-hidden', 'true');
        } else if (index > Math.max(currentIndex, targetIndex)) {
          slide.classList.add('is-below');
          slide.setAttribute('aria-hidden', 'true');
        } else {
          slide.setAttribute('aria-hidden', 'true');
        }
      });

      currentSlide.classList.add('is-leaving');
      nextSlide.classList.add('is-entering');
      nextSlide.setAttribute('aria-hidden', 'false');

      window.setTimeout(function () {
        currentIndex = targetIndex;
        isAnimating = false;
        setSettledClasses();
      }, animationDuration);

      announce();
      return true;
    }

    function isPinnedActive() {
      const rootRect = root.getBoundingClientRect();
      const stickyRect = sticky.getBoundingClientRect();
      const stickyTop = parseFloat(window.getComputedStyle(sticky).top) || 0;
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
      const stickyStartReached = rootRect.top <= stickyTop;
      const stickyEndAvailable = rootRect.bottom >= stickyTop + Math.min(stickyRect.height, viewportHeight);

      return stickyStartReached && stickyEndAvailable;
    }

    function handleStep(direction) {
      if (direction > 0) {
        return goTo(currentIndex + 1);
      }

      if (direction < 0) {
        return goTo(currentIndex - 1);
      }

      return false;
    }

    function handleWheel(event) {
      if (!isNearViewport || !isPinnedActive()) {
        wheelDelta = 0;
        return;
      }

      const direction = Math.sign(event.deltaY);
      const atBoundary = (direction > 0 && currentIndex === lastIndex) || (direction < 0 && currentIndex === 0);

      if (isAnimating) {
        event.preventDefault();
        return;
      }

      if (direction === 0 || atBoundary) {
        wheelDelta = 0;
        return;
      }

      event.preventDefault();
      wheelDelta += event.deltaY;

      if (Math.abs(wheelDelta) >= wheelThreshold) {
        const didChange = handleStep(Math.sign(wheelDelta));
        wheelDelta = 0;

        if (didChange) {
          stage.focus({ preventScroll: true });
        }
      }
    }

    function handleKeydown(event) {
      const activeInside = root.contains(document.activeElement);

      if (!activeInside && !isPinnedActive()) {
        return;
      }

      if (isAnimating) {
        event.preventDefault();
        return;
      }

      if (event.code === 'ArrowDown' || event.code === 'PageDown' || event.code === 'Space') {
        if (currentIndex < lastIndex) {
          event.preventDefault();
          handleStep(1);
        }
      } else if (event.code === 'ArrowUp' || event.code === 'PageUp') {
        if (currentIndex > 0) {
          event.preventDefault();
          handleStep(-1);
        }
      }
    }

    function handleTouchStart(event) {
      if (!event.touches.length) {
        return;
      }

      touchStartY = event.touches[0].clientY;
      touchDeltaY = 0;
    }

    function handleTouchMove(event) {
      if (!event.touches.length) {
        return;
      }

      touchDeltaY = event.touches[0].clientY - touchStartY;
      const direction = touchDeltaY < 0 ? 1 : -1;
      const atBoundary = (direction > 0 && currentIndex === lastIndex) || (direction < 0 && currentIndex === 0);

      if (isPinnedActive() && !atBoundary) {
        event.preventDefault();
      }
    }

    function handleTouchEnd() {
      if (!isPinnedActive() || isAnimating) {
        touchDeltaY = 0;
        return;
      }

      if (Math.abs(touchDeltaY) >= swipeThreshold) {
        handleStep(touchDeltaY < 0 ? 1 : -1);
      }

      touchDeltaY = 0;
    }

    if ('IntersectionObserver' in window) {
      observer = new window.IntersectionObserver(
        function (entries) {
          const entry = entries[0];
          isNearViewport = Boolean(entry && (entry.isIntersecting || entry.intersectionRatio > 0));
        },
        {
          rootMargin: '20% 0px 20% 0px',
          threshold: 0
        }
      );

      observer.observe(root);
    } else {
      isNearViewport = true;
    }

    window.addEventListener('wheel', handleWheel, wheelOptions);
    window.addEventListener('keydown', handleKeydown);
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, touchMoveOptions);
    window.addEventListener('touchend', handleTouchEnd);

    setSettledClasses();

    instances.set(root, {
      cleanup() {
        window.removeEventListener('wheel', handleWheel, wheelOptions);
        window.removeEventListener('keydown', handleKeydown);
        window.removeEventListener('touchstart', handleTouchStart);
        window.removeEventListener('touchmove', handleTouchMove);
        window.removeEventListener('touchend', handleTouchEnd);
        if (observer) {
          observer.disconnect();
        }
      }
    });
  }

  function destroy(root) {
    const instance = instances.get(root);

    if (!instance) {
      return;
    }

    instance.cleanup();
    instances.delete(root);
  }

  function initAll() {
    document.querySelectorAll(SELECTOR).forEach(setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    const root = event.target?.querySelector?.(SELECTOR) || event.target;

    if (root?.matches?.(SELECTOR)) {
      setup(root);
    }
  });

  document.addEventListener('shopify:section:unload', function (event) {
    const root = event.target?.querySelector?.(SELECTOR) || event.target;

    if (root?.matches?.(SELECTOR)) {
      destroy(root);
    }
  });
})();
